import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/pro-light-svg-icons";
import { useV2NotificationsUnreadCountQuery } from "@generated";
import useFlags from "@common/hooks/useFlags";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";

interface NotificationBellV2Props {
  expanded: boolean;
}

const NotificationBellV2: React.FC<NotificationBellV2Props> = ({
  expanded,
}) => {
  const flags = useFlags();
  const enabled = Boolean(flags.releaseNotificationManagement);
  const [open, setOpen] = useState(false);
  const { data } = useV2NotificationsUnreadCountQuery(undefined, {
    skip: !enabled,
  });

  if (!enabled) return null;

  const unreadCount = data?.["unread-count"] ?? 0;
  const hasUnread = unreadCount > 0;
  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
  const ariaLabel = hasUnread
    ? `Notifications, ${unreadCount} unread`
    : "Notifications";

  const button = (
    <button
      type="button"
      id="nav-notifications"
      aria-label={ariaLabel}
      onClick={() => setOpen(true)}
      style={{
        color: "rgba(255,255,255,0.7)",
        backgroundColor: "transparent",
        border: "none",
        boxSizing: "border-box",
        width: "calc(100% - 16px)",
        padding: expanded ? "10px 16px" : "10px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: expanded ? "flex-start" : "center",
        gap: expanded ? 12 : 0,
        borderRadius: 4,
        margin: "2px 8px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontSize: 14,
        fontFamily: "Open Sans, sans-serif",
        transition: "background-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor =
          "rgba(255,255,255,0.05)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
      }}
    >
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          flexShrink: 0,
        }}
      >
        <FontAwesomeIcon
          icon={faBell}
          style={{
            fontSize: expanded ? 16 : 20,
            width: expanded ? 16 : 20,
          }}
        />
        {hasUnread && (
          <span
            data-testid="notification-badge"
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -6,
              right: -8,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              boxSizing: "border-box",
              borderRadius: 8,
              backgroundColor: "#DC3545",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "16px",
              textAlign: "center",
            }}
          >
            {badgeLabel}
          </span>
        )}
      </span>
      {expanded && <span>Notifications</span>}
    </button>
  );

  return (
    <>
      {expanded ? (
        button
      ) : (
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id="tooltip-nav-notifications">Notifications</Tooltip>
          }
        >
          {button}
        </OverlayTrigger>
      )}
      {/* button2Text / handleButton2Click are structurally required: ScopeStackModal
          is untyped JS that destructures them without defaults, so tsc infers them as
          required props. We only use the single Close button — do not remove these. */}
      <ScopeStackModal
        show={open}
        modalTitle="Notifications"
        modalBody="Hello world"
        button1Text="Close"
        button2Text={undefined}
        handleClose={() => setOpen(false)}
        handleButton1Click={() => setOpen(false)}
        handleButton2Click={() => {}}
      />
    </>
  );
};

export default NotificationBellV2;
